type PartialRecord<K extends keyof any, T> = Partial<Record<K, T>>;
// type List = PartialRecord<'a' | 'b' | 'c', string>;

/**
 * A generic function signature that compares two items of type `T`. Such functions can be passed
 * as arguments to `Array.sort()`.
 */
export type Comparer<T> = (left: T, right: T) => number;

/**
 * A factory that generates a `Comparer<T>` that operates on a string property of `T`.
 */
export function createStringComparer<T extends PartialRecord<TKey, string>, TKey extends keyof T>(
	key: TKey,
	defaultValue = '',
): Comparer<T> {
	return (left: T, right: T): number =>
		(left[key] ?? defaultValue).localeCompare(right[key] ?? defaultValue);
}

/**
 * A factory that generates a `Comparer<T>` that operates on a numeric property of `T`.
 */
export function createNumberComparer<
	T extends PartialRecord<TKey, number | boolean>,
	TKey extends keyof T
>(key: TKey, defaultValue: number | boolean = 0): Comparer<T> {
	return (left: T, right: T): number =>
		Number(left[key] ?? defaultValue) - Number(right[key] ?? defaultValue);
}

/**
 * A factory that generates a `Comparer<T>` that operates on a numeric property of `T`.
 */
export function createDateComparer<
	T extends PartialRecord<TKey, Date | number | string>,
	TKey extends keyof T
>(key: TKey, defaultValue: Date | number | string = 0): Comparer<T> {
	return (left: T, right: T): number =>
		new Date(left[key] ?? defaultValue).getTime() -
		new Date(right[key] ?? defaultValue).getTime();
}

/**
 * A type that returns a string literal union of properties from `TRow` that are eligible for
 * sorting.
 */
export type SortableColumn<TRow, TSortableColumn extends keyof TRow = keyof TRow> = keyof Pick<
	TRow,
	TSortableColumn
>;

/**
 * A structure that configures how a single column is sorted.
 */
export interface SortableColumnConfig<TRow> {
	comparer: Comparer<TRow>;
	descending: boolean;
}

/**
 * An aggregate mapping of sortable columns to their sort configuration.
 */
export type TableSorterConfig<TRow, TSortableColumn extends keyof TRow> = Record<
	TSortableColumn,
	SortableColumnConfig<TRow>
>;

/**
 * A reusable implementation of table sorting.
 *
 * @example <caption>Here's an example of how to utilize this type in a Vue component:</caption>
 * ```typescript
 * interface Person {
 *     name: string;
 *     birthDate: Date;
 *     mother: Person;
 *     father: Person;
 * }
 *
 * // Only `name` and `birthDate` are capable of being sorted in this example.
 * type PersonSortableColumn = SortableColumn<Person, 'name' | 'birthDate'>;
 *
 * @Component({ name: 'PeopleTable' })
 * class PeopleTable extends Vue {
 *     @Prop() people: Person[];
 *
 *     // Create an instance of TableSorter with per-column sort configurations and the initial
 *     // column to sort.
 *     readonly sorter = new TableSorter<Person, PersonSortableColumn>(
 *         {
 *             name: {
 *                 comparer: createStringComparer<Person, 'name'>('name'),
 *                 descending: false,
 *             },
 *             birthDate: {
 *                 comparer: createDateComparer<Person, 'birthDate'>('birthDate'),
 *                 descending: true,
 *             }
 *         },
 *         'name',
 *     );
 *
 *     // Provide a computed property that sorts the list of people based on the current sort
 *     // configuration. If filtering is necessary, that could also be performed in this accessor
 *     // before sorting and then pass `false` for the `sortInPlace` argument to avoid an
 *     // unnecessary cloning of the array.
 *     get sortedPeople(): Person[] {
 *         return this.sorter.sort(this.people.filter(\/\* ... \*\/), true);
 *     }
 *
 *     // Provide an event handler to update the sort configuration.
 *     tableHeaderClicked(column: PersonSortableColumn): void {
 *         this.sorter.column = column;
 *     }
 *
 *     // Optional: Implement a method that will adjust styling of sort indicators based on the
 *     // current sort configuration.
 * 	   getSortIndicatorClasses(column: PersonSortableColumn): string {
 *         return column === this.sorter.column
 *             ? `fas ${this.sorter.config[column].descending ? 'fa-sort-down' : 'fa-sort-up'}`
 *             : 'd-none';
 *      }
 * }
 * ```
 */
export class TableSorter<TRow, TSortableColumn extends keyof TRow = keyof TRow> {
	public constructor(
		public readonly config: Readonly<TableSorterConfig<TRow, TSortableColumn>>,
		initialColumn: TSortableColumn,
	) {
		this._column = initialColumn;
	}

	public get column(): TSortableColumn {
		return this._column;
	}
	public set column(value: TSortableColumn) {
		if (!(value in this.config)) {
			throw new TypeError(`Invalid sortable column '${value}'`);
		}

		if (value !== this._column) {
			this._column = value;
		} else {
			this.config[value].descending = !this.config[value].descending;
		}
	}

	/**
	 * Orders a given array according to the current sort configuration.
	 *
	 * @param table The array to sort.
	 * @param sortInPlace When true, sorts the given `table` array directly; when false, clones
	 * `table` before sorting. Defaults to the safest option of cloning before sorting.
	 */
	public sort(table: TRow[], sortInPlace: boolean = false): TRow[] {
		const columnConfig: SortableColumnConfig<TRow> = this.config[this.column];

		const array: TRow[] = sortInPlace ? table : table.slice();
		return array.sort((left: TRow, right: TRow): number => {
			const compareResult: number = columnConfig.comparer(left, right);
			return columnConfig.descending ? -compareResult : compareResult;
		});
	}

	private _column: TSortableColumn;
}
